import { z } from 'zod';

/**
 * Konteks "kenapa rate dynamic pricing jadi segini" dari API
 * (`dynamic_pricing_info` pada `/v4/price-adjustment`).
 *
 * SEMUA field sengaja `.nullish()` dan objeknya sendiri opsional supaya:
 * 1. Response API versi LAMA (yang belum punya field ini) tetap lolos validasi
 *    — penting saat FE sudah deploy tapi BE belum, atau sebaliknya.
 * 2. Backend bisa mengirim `null` untuk property yang tidak punya dynamic
 *    pricing group aktif.
 */
const DynamicPricingInfoSchema = z.object({
    /**
     * no_dynamic_pricing_group | no_pricing_item | last_minute_restriction |
     * excluded_period | not_in_included_period | no_matching_rule | rule_applied
     */
    reason_code: z.string().nullish(),
    calculation_method: z.string().nullish(),
    /** Rate hasil rule SEBELUM dijepit min/max override. */
    rate_before_override: z.string().nullish(),
    min_rate_override: z.string().nullish(),
    max_rate_override: z.string().nullish(),
    min_rate_override_applied: z.coerce.boolean().nullish(),
    max_rate_override_applied: z.coerce.boolean().nullish(),
    last_minute_restriction: z.coerce.boolean().nullish(),
    last_minute_days: z.coerce.number().nullish(),
    /** Tanggal paling awal yang boleh kena dynamic pricing. */
    last_minute_cutoff_date: z.string().nullish(),
    min_stay_restriction: z.coerce.boolean().nullish(),
    min_stay_nights: z.coerce.number().nullish(),
    item_min_stay_restriction: z.coerce.boolean().nullish(),
});

const PriceAdjustmentDetailSchema = z.object({
    id: z.string().uuid().nullable(),
    rate_plan_id: z.string().uuid(),
    room_type_id: z.string().uuid(),
    date: z.string().refine((val) => !isNaN(new Date(val) as any), {
        message: 'Invalid date format',
    }),
    rate: z.string(),
    max_stay: z.number().int(),
    min_stay_through: z.number().int(),
    min_stay_arrival: z.number().int(),
    closed_to_arrival: z.coerce.boolean(),
    closed_to_departure: z.coerce.boolean(),
    stop_sell: z.coerce.boolean(),
    occupancy: z.coerce.number(),
    total_booked: z.number().int(),
    total_rooms: z.number().int(),
    available: z.number().int(),
    original_rate: z.string(),
    adjustment_message: z.string().nullable(),
    rule_start: z.number().int().nullable(),
    rule_end: z.number().int().nullable(),
    rule_adjustment_percentage: z.coerce.number().nullable(),
    is_dynamic_pricing: z.boolean(),
    dynamic_pricing_info: DynamicPricingInfoSchema.nullish(),
});

const PriceAdjustmentDataSchema = z.record(z.string().uuid(), z.record(z.string(), PriceAdjustmentDetailSchema));

export const PriceAdjustmentResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number().int(),
    data: PriceAdjustmentDataSchema,
});

export type PriceAdjustmentResponse = z.infer<typeof PriceAdjustmentResponseSchema>;
export type PriceAdjustmentData = z.infer<typeof PriceAdjustmentDetailSchema>;
export type DynamicPricingInfo = z.infer<typeof DynamicPricingInfoSchema>;